import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';
import DatePickerToggler from 'common/custom-components/lib/components/DatePickerToggler';
import TessellInput from 'common/custom-components/lib/components/TessellInput';
import TessellModal from 'common/custom-components/lib/components/TessellModal';
import moment from 'moment';
import { styles } from './styles';
import useSilenceAlertsModel from './useSilenceAlerts';
import { OTP_TYPE } from 'constants/otpType';
import OtpModal from '../OtpModal';
import { ChipListing } from 'common/ChipListing/ChipListing';
import LoadingOverlay from 'common/custom-components/lib/components/LoadingOverlay';
import TessellAutocomplete from 'common/custom-components/lib/components/TessellAutocomplete';
import BulkServiceIdSelect from './BulkServiceIdSelect';

type SilenceAlertsModalProps = {
  open: boolean;
  setOpen: any;
  selectedAlertLabels?: any;
  hideInputs?: boolean;
  bulkSilence?: boolean;
  handleOnSuccess: () => void;
  includeDomain?: boolean;
  silenceAlertsURL?: string;
  useEnhancedSelect?: boolean;
};

const rangeType = {
  '1h': '1 hour',
  '3h': '3 hour',
  '6h': '6 hour',
  '12h': '12 hour',
  '24h': '24 hour',
  Custom: 'Custom',
};

const startMin = moment(new Date());

export const SilenceAlertsModal = (props: SilenceAlertsModalProps) => {
  const {
    open,
    setOpen,
    selectedAlertLabels,
    hideInputs = false,
    bulkSilence = false,
    handleOnSuccess,
    includeDomain = false,
    silenceAlertsURL,
    useEnhancedSelect = false,
  } = props;

  const {
    inputs,
    isLoading,
    openOTP,
    sendOTPIsLoading,
    step,
    disableSilence,
    tenants,
    selectedTenant,
    entitiesMap,
    entitiesLoading,
    handleChange,
    setOpenOTP,
    setStep,
    handleConfirm,
    otpSubmitHandler,
    handleDateToggle,
    updateTimeUsingNativePicker,
    range,
    serviceIdOptions,
  } = useSilenceAlertsModel({
    selectedAlertLabels,
    handleOnSuccess,
    bulkSilence,
    includeDomain,
    silenceAlertsURL,
    tenantDomain: selectedAlertLabels?.domain || null,
  });

  const onClose = () => {
    setOpen(false);
  };

  const DialogTitle = () => <Typography variant="h5">Silence Alert</Typography>;

  const handleTenantChange = (newValue) => {
    const selectedTenantObject = tenants.find(
      (tenant) => tenant.name === newValue,
    );
    handleChange({
      target: {
        name: 'tenant_id',
        value: selectedTenantObject || '',
      },
    });
  };

  const handleServiceIdChange = (newValue) => {
    handleChange({
      target: {
        name: 'service_id',
        value: newValue || '',
      },
    });
  };

  const handleServiceInstanceIdChange = (newValue) => {
    handleChange({
      target: {
        name: 'service_instance_id',
        value: newValue || '',
      },
    });
  };

  const handleSubscriptionIdChange = (newValue) => {
    handleChange({
      target: {
        name: 'service_subscription_id',
        value: newValue || '',
      },
    });
  };

  const handleComputeResourceIdChange = (newValue) => {
    handleChange({
      target: {
        name: 'compute_resource_id',
        value: newValue || '',
      },
    });
  };

  const handleEksNamespaceChange = (newValue) => {
    handleChange({
      target: {
        name: 'eks_namespace',
        value: newValue || '',
      },
    });
  };

  const DialogContent = () => {
    return (
      <>
        <Stack sx={styles.form}>
          {selectedAlertLabels ? (
            <ChipListing data={selectedAlertLabels} />
          ) : (
            ''
          )}
          {!hideInputs ? (
            <Stack width="100%" alignItems="center" alignSelf="center" m={3}>
              <TessellAutocomplete
                sx={styles.formInput}
                name="tenant_id"
                label="Tenant Id (Required)"
                value={selectedTenant?.name || ''}
                onChange={handleTenantChange}
                placeholder="All alerts of this tenant will be silenced"
                options={tenants.map((tenant) => ({
                  label: tenant.name,
                  value: tenant.name,
                }))}
                fullWidth
              />
            </Stack>
          ) : (
            <></>
          )}
          {!hideInputs && !bulkSilence && entitiesMap ? (
            <LoadingOverlay isLoading={entitiesLoading}>
              <Stack spacing={3}>
                <TessellAutocomplete
                  sx={styles.formInput}
                  name="service_id"
                  label="Service Id"
                  value={inputs?.service_id || ''}
                  onChange={handleServiceIdChange}
                  placeholder="All alerts of this service will be silenced"
                  options={
                    entitiesMap?.service?.map((v) => ({
                      label: `${v?._id} (${v?.name})`,
                      value: v?._id,
                    })) || []
                  }
                  fullWidth
                />
                <TessellAutocomplete
                  sx={styles.formInput}
                  name="service_instance_id"
                  label="Service Instance Id"
                  value={inputs?.service_instance_id || ''}
                  onChange={handleServiceInstanceIdChange}
                  placeholder="All alerts of this service instance will be silenced"
                  options={
                    entitiesMap?.instance?.map((v) => ({
                      label: v,
                      value: v,
                    })) || []
                  }
                  fullWidth
                />
                <TessellAutocomplete
                  sx={styles.formInput}
                  name="service_subscription_id"
                  label="Subscription Id"
                  value={inputs?.service_subscription_id || ''}
                  onChange={handleSubscriptionIdChange}
                  placeholder="All alerts of this subscription will be silenced"
                  options={
                    entitiesMap?.subscriptions?.map((v) => ({
                      label: `${v?._id} (${v?.name})`,
                      value: v?._id,
                    })) || []
                  }
                  fullWidth
                />
                <TessellInput
                  sx={styles.formInput}
                  name="alertname"
                  label="Alert Name"
                  value={inputs?.alertname || ''}
                  onChange={handleChange}
                  placeholder="All alerts of this type will be silenced"
                />
                <TessellAutocomplete
                  sx={styles.formInput}
                  name="compute_resource_id"
                  label="Compute Resource Id"
                  value={inputs?.compute_resource_id || ''}
                  onChange={handleComputeResourceIdChange}
                  placeholder="All alerts with this compute resource will be silenced"
                  options={
                    entitiesMap?.compute?.map((v) => ({
                      label: v,
                      value: v,
                    })) || []
                  }
                  fullWidth
                />
                <TessellInput
                  sx={styles.formInput}
                  name="job"
                  label="Job Name"
                  value={inputs?.job || ''}
                  onChange={handleChange}
                  placeholder="All alerts with this job name will be silenced"
                />
                <TessellAutocomplete
                  sx={styles.formInput}
                  name="eks_namespace"
                  label="EKS Namespace"
                  value={inputs?.eks_namespace || ''}
                  onChange={handleEksNamespaceChange}
                  placeholder="All alerts from this EKS Namespace will be silenced"
                  options={
                    entitiesMap?.eksNamespace?.map((v) => ({
                      label: v,
                      value: v,
                    })) || []
                  }
                  fullWidth
                />
                <TessellInput
                  sx={styles.formInput}
                  name="mountpoint"
                  label="Mountpoint"
                  value={inputs?.mountpoint || ''}
                  onChange={handleChange}
                  placeholder="All alerts of this mountpoint will be silenced"
                />
              </Stack>
            </LoadingOverlay>
          ) : (
            <></>
          )}
          {!hideInputs && selectedTenant && bulkSilence ? (
            <LoadingOverlay isLoading={entitiesLoading}>
              {useEnhancedSelect ? (
                <BulkServiceIdSelect
                  serviceIdOptions={serviceIdOptions}
                  selectedIds={inputs?.serviceIds || ''}
                  onChange={handleChange}
                />
              ) : (
                <Stack sx={{ mt: 3 }}>
                  <TessellAutocomplete
                    label="Service Ids"
                    value={inputs?.serviceIds || []}
                    variant="outlined"
                    size="large"
                    onChange={handleChange}
                    fullWidth
                    options={serviceIdOptions}
                    name="service_id"
                    multiple
                  />
                </Stack>
              )}
            </LoadingOverlay>
          ) : (
            <></>
          )}

          <Stack
            direction="row"
            spacing={1}
            sx={{ ...styles.timeSelectionFrom, mt: 4 }}
          >
            <Typography>Silence alerts for</Typography>
            <DatePickerToggler
              rangeType={rangeType}
              startTime={inputs?.startsAt}
              endTime={inputs?.endsAt}
              updateTimeUsingNativePicker={updateTimeUsingNativePicker}
              startMin={startMin}
              handleOnChange={handleDateToggle}
              range={range}
              additionalInitialSettings={{ drops: 'up' }}
            />
          </Stack>
        </Stack>
        <OtpModal
          open={openOTP}
          setOpen={setOpenOTP}
          onSubmit={otpSubmitHandler}
          isSubmitting={step === 1 ? sendOTPIsLoading : isLoading}
          otpUser="Ops admin"
          step={step}
          setStep={setStep}
          reasonSubmitBtnText="Submit"
          reasonHeading="Enter reason for silencing alerts (with JIRA link)"
          otpType={OTP_TYPE.SILENCE_ALERTS}
        />
      </>
    );
  };

  const DialogActions = () => {
    return (
      <>
        <Button
          onClick={() => {
            setOpen(false);
            setStep(1);
          }}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleConfirm}
          size="small"
          variant="contained"
          loading={false}
          disabled={disableSilence}
        >
          Silence
        </LoadingButton>
      </>
    );
  };

  const renderFunctions = {
    title: DialogTitle,
    content: DialogContent,
    actions: DialogActions,
  };

  return (
    <TessellModal
      renderFunctions={renderFunctions}
      maxWidth="md"
      open={open}
      onClose={onClose}
    />
  );
};
